/*
 * 一覧用の便利関数
 */
import draggable from 'vuedraggable'
import RepositoryFactory from '@/repositories/RepositoryFactory'

export default {
  components: { draggable },
  data () {
    return {
      // 一覧データ
      list: [],
      // 選択中ID
      checkedIds: [],
      // 選択中アイテム
      checkedItems: [],
      // 選択可能最大数
      checkLimit: 100000,
      /**
       * {string} 全件選択モード
       * null, 'thisPage', 'allPages'
       * 個別に選択を切り替えたら、たとえ全件選択されようがOFFにする（Gmailと同じ）
       */
      checkAllMode: null,
      // 検索条件
      conditions: {},
      // ページング
      pagination: {},
      // ソート（検索条件だけ初期化したい時があるので、conditionから抜き出しておく）
      sort: {},

      /**
       * storeに検索条件を保存するかどうか
       * storeを使わない場合はcreatedなどでfalseにしてください
       */
      useStoreConds: true
    }
  },
  computed: {
    isAllPagesChecked () {
      return this.checkAllMode === 'allPages'
    }
  },
  watch: {
    checkedIds (checkedIds) {
      const vm = this
      let newItems = []
      // 削除
      this.checkedItems = this.checkedItems.filter(function (item) {
        return vm.isIncludes(checkedIds, item.id)
      })

      // 追加
      for (let id of checkedIds) {
        // すでに選択済みでない場合
        if (!this.checkedItems.some(function (item) {
          return id === item.id
        })) {
          // 選択したアイテムを追加する
          // IE11 find() 未実装
          // newItems.push(this.list.find(function (item) {
          //   console.log(id)
          //   return id === item.id
          // }))

          // find() 互換処理
          for (let item of this.list) {
            if (id === item.id) {
              newItems.push(item)
              break
            }
          }
        }
      }
      this.checkedItems = this.checkedItems.concat(newItems)
    }
  },
  methods: {
    // ------------------------------------------------------------
    // checkbox関連
    // ------------------------------------------------------------
    /**
     * 複数レコードのチェックボックスをON/OFFする
     * @param {Array} list 画面に表示中の一覧
     *          - Object {id: rquired}
     * @param {Boolean|Number} checked 設定したいON/OFF状態
     *          - false, 0: OFF
     *          - true, 1: ON (このページの全選択)
     *          - 2: ON (全ページの全選択)
     */
    checkAll (list, checked) {
      checked = +checked // 数値変換
      this.setCheckAllMode([null, 'thisPage', 'allPages'][checked])

      // 別ページのデータを対象データにしないように、現ページの分しかチェックしない
      this.checkedIds = []
      if (checked) {
        for (let index = 0, length = list.length; index < length; index++) {
          this.checkedIds.push(list[index].id)
          // 超過分を消す
          if (this.checkedIds.length > this.checkLimit) {
            this.checkedIds.shift()
          }
        }
      }
    },
    /**
     * 全件選択のモードを切り替える
     * @param {String} モード（null, 'thisPage', 'allPages'）
     */
    setCheckAllMode (mode) {
      this.checkAllMode = mode || null
    },
    /**
     * あるレコードのチェックボックスをON/OFFする
     * @param id
     * @param {Boolean} checked 設定したいON/OFF状態
     */
    checkItem (id, checked) {
      this.setCheckAllMode()

      // remove
      let indexOfIds = this.checkedIds.indexOf(id)
      if (indexOfIds >= 0) this.checkedIds.splice(indexOfIds, 1)
      // then insert
      if (checked) {
        this.checkedIds.push(id)
      }
      // 超過分を消す
      if (this.checkedIds.length > this.checkLimit) {
        this.checkedIds.shift()
      }
    },
    /**
     * あるレコードの選択状態を取得する
     * @param id
     * @return Boolean
     */
    isChecked (id) {
      return this.checkedIds.indexOf(id) >= 0
    },
    /**
     * 選択されているレコードがあるかどうかを取得する
     * @return Boolean
     */
    hasCheckedItems () {
      return this.checkedIds.length > 0
    },
    // ------------------------------------------------------------
    // 検索条件
    // ------------------------------------------------------------
    /**
     * 現在の検索条件を取得する
     */
    loadListConds () {
      if (!this.useStoreConds) return // flag
      let conds = this.$store.getState('listConds')
      this.pagination = conds.pagination
      this.conditions = conds.conditions
      this.sort = {
        sort_item: this.conditions.sort_item || '',
        direction: this.conditions.direction || ''
      }
    },
    /**
     * 現在の検索条件を保存する
     * 一覧取得後に呼ばれる
     */
    setListConds (conds) {
      if (!this.useStoreConds) return // flag
      this.$store.commit('setListConds', {
        target: '',
        data: {
          pagination: this.pagination,
          conditions: this.conditions
        }
      })
    },
    // ------------------------------------------------------------
    // 一覧処理
    // ------------------------------------------------------------
    /**
     * 一覧取得の共通処理
     * @param {Object} options
     * @param {String} options.repo - RepositoryFactory.getに使う名前
     * @param {String} options.method - 実行するリポジトリのメソッド名
     * @param {Object} options.params - パラメータ
     * @param {boolean} options.silent - エラーメッセージを初期化しない
     * @param {function} options.onSuccess - 通信成功時のコールバック関数
     * @param {function} options.onError - 通信失敗時のコールバック関数
     * @param {function} options.finally - 通信終了時に必ず実行するコールバック関数
     */
    sendListRequest (options) {
      const vm = this
      const silent = options.silent ? options.silent : false

      // サイレントではない場合（通常の場合）
      if (!silent) {
        // メッセージと一覧をクリアする
        this.clearMessages()
        this.list = []
        this.checkAll([])
      }

      RepositoryFactory.get(options.repo)[options.method](options.params).then(function (response) {
        if (!vm.isApiSuccess(response)) return
        let data = vm.getApiData(response)
        vm.list = data.data
        vm.pagination = data.pagination
        vm.conditions = options.params
        // 現状保存
        vm.setListConds()
        if ('onSuccess' in options) {
          options['onSuccess'](response)
        }
      })
        // エラー時処理
        .catch(function (error) {
          // 最終ページが喪失した場合は1つ前の一覧を表示する
          if (error.response && error.response.status === 404 && vm.pagination.current_page > 1) { // this.isErrorNotFound
            vm.pagination = {
              current_page: vm.pagination.current_page - 1
            }
            vm.getList({page: vm.pagination.current_page})
          }
          else {
            vm.setErrorMessage(error)
            vm.clearPagination()
            if ('onError' in options) {
              options['onError'](error.response)
            }
          }
        })
        .finally(function () {
          if ('finally' in options) {
            options['finally']()
          }
        })
    },
    /**
     *
     * @param {string} repo: RepositoryFactory.getに使う名前
     * @param {string} method name
     * @param {object} options
     *          - params: Object
     *          - success: Function
     */
    sendBulkRequest (repo, method, options) {
      const vm = this

      // 現在表示中のページに含まれる項目のみ削除する
      options.params.id = options.params.id.filter(function (id) {
        for (let item of vm.list) {
          if (item.id === id) {
            return true
          }
        }
      })

      RepositoryFactory.get(repo)[method](options.params)
        .then(function (response) {
          // checkedを空にする
          if ('id' in options.params && vm.checkedIds.length) {
            vm.checkedIds = []
          }

          if (!vm.isApiSuccess(response)) return
          vm.setSuccessMessage(response)

          if ('onSuccess' in options) {
            options['onSuccess'](options, response)
          }

          vm.getList({page: vm.pagination.current_page})
        })
        .catch(function (error) {
          vm.setErrorMessage(error)

          if ('onError' in options) {
            options['onError'](error.response)
          }
        })
    },
    clearPagination () {
      this.list = []
      this.pagination = {}
    },

    // 以上未整理
    // 以下新デザインで追加
    /**
     * 一覧をソートする
     */
    sortList (sort) {
      this.sort = sort
      this.getList({
        page: 1, // ページは先頭に戻す
        sort
      })
    },
    /**
     * accesslogで使ってる？
     * @param {Object} conds 検索条件
     */
    search (conds) {
      this.getList({
        page: this.pagination.current_page,
        sort: this.sort,
        conds
      })
    }
  },
  created () {
    // 保持している条件があれば適用する
    this.loadListConds()

    /**
     * app-list-sortableで使ってるElement.closestのIE用Polyfill
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/closest
     */
    if (!Element.prototype.matches) {
      Element.prototype.matches = Element.prototype.msMatchesSelector ||
                                    Element.prototype.webkitMatchesSelector
    }

    if (!Element.prototype.closest) {
      Element.prototype.closest = function (s) {
        let el = this
        if (!document.documentElement.contains(el)) return null
        do {
          if (el.matches(s)) return el
          el = el.parentElement || el.parentNode
        } while (el !== null && el.nodeType === 1)
        return null
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/list.js